import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  ShoppingCart,
  MapPin,
  CalendarClock,
  Boxes,
  Scale,
  PenTool,
  Sparkles,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile : transit + intégrité ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison à domicile de vos cartons de déménagement à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Épargnez-vous la manutention contraignante de liasses de carton
            brut à faire entrer de force dans l&apos;habitacle de votre
            voiture. Nous déployons notre propre logistique pour déposer
            votre commande directement à votre adresse à {city.name}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Transit sans intermédiaire",
              body: "Nous opérons exclusivement avec nos équipes et notre propre flotte de véhicules en Île-de-France. Aucun réseau de messagerie tiers ne manipule vos produits, éliminant tout risque de détérioration avant l'assemblage.",
              Icon: Truck,
            },
            {
              title: "Garantie d'intégrité",
              body: "Nos véhicules hermétiques préservent vos fournitures de l'humidité et des salissures. Vous réceptionnez un matériel impeccable, rigide et immédiatement opérationnel pour vos préparatifs.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — 3 règles d'or (cartes horizontales avec gros numéros) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
            Conseils d&apos;experts
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            3 règles d&apos;or pour organiser vos cartons de déménagement
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour transformer la corvée de l&apos;emballage en une mécanique
            fluide et sans stress, nos spécialistes vous dictent trois
            règles d&apos;or d&apos;organisation.
          </p>
        </div>

        <div className="mt-10 space-y-4">
          {[
            {
              n: "01",
              tag: "La stratégie du vide",
              title: "Le calage actif",
              body: "Un contenant à moitié plein est un contenant en danger. Utilisez nos consommables pour combler la moindre poche d'air interne. Bloquer le mouvement des objets à l'intérieur de la boîte est le secret absolu pour éliminer la casse lors des virages ou des freinages.",
              Icon: Boxes,
            },
            {
              n: "02",
              tag: "La loi de la gravité",
              title: "La hiérarchie des masses",
              body: "Répartissez vos biens par densité. Le lourd se place invariablement au fond des petits volumes pour préserver votre dos, tandis que les matières volumineuses et légères (textiles, couettes) colonisent les grands formats.",
              Icon: Scale,
            },
            {
              n: "03",
              tag: "Le codage périphérique",
              title: "Signalétique latérale",
              body: "Ne notez jamais le contenu sur le dessus du carton. Inscrivez la pièce de destination sur les parois latérales. Lorsque vos modules seront gerbés (empilés) dans le camion ou votre futur logement, l'information restera immédiatement visible au premier coup d'œil.",
              Icon: PenTool,
            },
          ].map(({ n, tag, title, body, Icon }) => (
            <div
              key={n}
              className="grid grid-cols-1 md:grid-cols-12 items-stretch bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] overflow-hidden"
            >
              {/* Big number column */}
              <div className="md:col-span-2 flex items-center justify-center bg-[#237cb5] text-white py-6 md:py-0">
                <span className="font-display font-extrabold text-5xl md:text-6xl tracking-tight text-white">
                  {n}
                </span>
              </div>
              {/* Content column */}
              <div className="md:col-span-10 p-6 md:p-7">
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-white border border-[#cfdbe6] text-[#237cb5]">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#FF7A00]">
                      {tag}
                    </div>
                    <h3 className="font-display font-extrabold text-[17px] text-[#0f1d2b]">
                      {title}
                    </h3>
                  </div>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-garches-cta-packs"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir nos kits cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-garches-cta-protection"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir les consommables de calage
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Parcours d'achat (3 étapes horizontales avec icônes) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Parcours d&apos;achat
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment commander vos cartons en quelques clics
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Nous avons dépouillé notre interface de tout superflu pour vous
            offrir une expérience d&apos;approvisionnement ultra-rapide.
          </p>
        </div>

        <ol className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 relative">
          {[
            {
              n: "01",
              title: "Sélectionnez votre configuration",
              body: "Naviguez parmi nos modules spécialisés ou laissez-vous guider par nos solutions packagées, calibrées d'après la superficie de votre espace de vie.",
              Icon: ShoppingCart,
            },
            {
              n: "02",
              title: "Validez votre point de dépose",
              body: `Confirmez votre panier et spécifiez vos coordonnées précises à ${city.name}.`,
              Icon: MapPin,
            },
            {
              n: "03",
              title: "Planifiez la réception",
              body: "Notre cellule logistique s'active instantanément pour caler le passage de notre chauffeur selon vos disponibilités de planning.",
              Icon: CalendarClock,
            },
          ].map(({ n, title, body, Icon }) => (
            <li
              key={n}
              className="relative bg-white rounded-2xl border border-[#cfdbe6] p-6 md:p-7"
            >
              <div className="absolute -top-4 left-6 inline-flex items-center justify-center h-9 px-3 rounded-full bg-[#FF7A00] text-white font-display font-extrabold text-[13px] tracking-[0.1em]">
                ÉTAPE {n}
              </div>
              <div className="mt-3 flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-10">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-garches-cta-shop"
          >
            <ShoppingCart className="h-4 w-4" strokeWidth={2.6} />
            Configurer mon panier
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-garches-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
